<section class="mt-2">
  <div class="mx-auto max-w-4xl">
    @if (isLoading()) {
      <p class="text-sm text-text-muted">
        {{
          isCreateMode
            ? 'Preparing new numeron form...'
            : isAddToCollectionMode
              ? 'Preparing add numeron form...'
              : 'Loading numeron details...'
        }}
      </p>
    } @else if (!canEdit()) {
      <p class="text-sm text-danger">{{ loadErrorMessage() }}</p>
    } @else {
      <form class="grid gap-4" #numeronForm="ngForm" (ngSubmit)="onSubmit(numeronForm)" novalidate>
        <article
          class="overflow-hidden rounded-lg bg-white shadow-xl shadow-black/10 ring-1 ring-black/5"
        >
          <header class="border-b border-subtle bg-surface px-6 py-4">
            <h1 class="m-0 text-xl font-semibold leading-tight text-text">
              {{
                isCreateMode
                  ? 'New Numeron'
                  : isAddToCollectionMode
                    ? 'Add Numeron'
                    : 'Edit Numeron'
              }}
            </h1>
            @if (isAddToCollectionMode && collectionContext(); as context) {
              <p class="m-0 mt-1 text-sm text-text-muted">Collection: {{ context.title }}</p>
            }
          </header>

          <div class="grid gap-6 px-6 py-5">
            <p class="text-sm text-text-muted">
              Required fields are marked with <span class="font-semibold text-danger">*</span>.
            </p>

            @if (showValidationSummary(numeronForm)) {
              <p
                class="rounded-lg border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger"
              >
                Please fix the highlighted fields and try again.
              </p>
            }

            <div class="grid gap-2">
              <label class="text-sm font-medium text-text" for="numeral">
                Numeral <span class="text-danger">*</span>
              </label>
              <input
                id="numeral"
                #numeralModel="ngModel"
                [(ngModel)]="formModel.numeral"
                class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                [class.border-danger]="
                  numeralModel.invalid && (numeralModel.touched || numeronForm.submitted)
                "
                name="numeral"
                placeholder="Enter Numeral"
                required
                type="text"
              />
              @if (numeralModel.invalid && (numeralModel.touched || numeronForm.submitted)) {
                <p class="text-sm text-danger">Numeral is required.</p>
              }
            </div>

            <div class="grid gap-2">
              <label class="text-sm font-medium text-text" for="title">
                Title <span class="text-danger">*</span>
              </label>
              <textarea
                id="title"
                #titleModel="ngModel"
                [(ngModel)]="formModel.title"
                class="min-h-28 w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                [class.border-danger]="
                  titleModel.invalid && (titleModel.touched || numeronForm.submitted)
                "
                name="title"
                placeholder="Enter Title"
                required
                rows="3"
              ></textarea>
              @if (titleModel.invalid && (titleModel.touched || numeronForm.submitted)) {
                <p class="text-sm text-danger">Title is required.</p>
              }
            </div>

            <label
              class="inline-flex items-start gap-3 rounded-lg border border-subtle bg-surface px-4 py-3 text-sm text-text"
              for="visibility"
            >
              <input
                id="visibility"
                [(ngModel)]="formModel.visibility"
                class="mt-0.5 h-4 w-4 rounded border-subtle text-primary focus:ring-primary"
                name="visibility"
                type="checkbox"
              />
              <span>Check this box if you don't want anyone else to see this Numeron</span>
            </label>

            <div class="overflow-hidden rounded-xl border border-subtle bg-surface shadow-md">
              <div class="border-b border-subtle bg-surface px-4 py-4 sm:px-5">
                <div class="mb-3">
                  <p class="m-0 text-sm font-semibold text-text">Content Details</p>
                  <p class="m-0 mt-1 text-sm text-text-muted">
                    Switch between sections to update media, source, categories, and actions.
                  </p>
                  <p class="m-0 mt-1 text-sm text-text-muted">
                    Media and Topic are required to save this Numeron.
                  </p>
                </div>

                <div
                  class="grid grid-cols-2 gap-2 rounded-xl bg-white/70 p-2 text-sm shadow-sm sm:grid-cols-4"
                  role="tablist"
                  aria-label="Edit numeron sections"
                >
                  <button
                    type="button"
                    class="rounded-lg border px-3 py-2.5 font-medium transition duration-200 ease-in-out"
                    [class.border-primary]="isActiveTab('media')"
                    [class.bg-primary]="isActiveTab('media')"
                    [class.text-primary-contrast]="isActiveTab('media')"
                    [class.shadow-sm]="isActiveTab('media')"
                    [class.border-transparent]="!isActiveTab('media')"
                    [class.bg-transparent]="!isActiveTab('media')"
                    [class.text-text-muted]="!isActiveTab('media')"
                    [class.hover:border-subtle]="!isActiveTab('media')"
                    [class.hover:bg-surface]="!isActiveTab('media')"
                    [class.hover:text-text]="!isActiveTab('media')"
                    [attr.aria-selected]="isActiveTab('media')"
                    (click)="setActiveTab('media')"
                    role="tab"
                  >
                    Media
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border px-3 py-2.5 font-medium transition duration-200 ease-in-out"
                    [class.border-primary]="isActiveTab('source')"
                    [class.bg-primary]="isActiveTab('source')"
                    [class.text-primary-contrast]="isActiveTab('source')"
                    [class.shadow-sm]="isActiveTab('source')"
                    [class.border-transparent]="!isActiveTab('source')"
                    [class.bg-transparent]="!isActiveTab('source')"
                    [class.text-text-muted]="!isActiveTab('source')"
                    [class.hover:border-subtle]="!isActiveTab('source')"
                    [class.hover:bg-surface]="!isActiveTab('source')"
                    [class.hover:text-text]="!isActiveTab('source')"
                    [attr.aria-selected]="isActiveTab('source')"
                    (click)="setActiveTab('source')"
                    role="tab"
                  >
                    Source
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border px-3 py-2.5 font-medium transition duration-200 ease-in-out"
                    [class.border-primary]="isActiveTab('categories')"
                    [class.bg-primary]="isActiveTab('categories')"
                    [class.text-primary-contrast]="isActiveTab('categories')"
                    [class.shadow-sm]="isActiveTab('categories')"
                    [class.border-transparent]="!isActiveTab('categories')"
                    [class.bg-transparent]="!isActiveTab('categories')"
                    [class.text-text-muted]="!isActiveTab('categories')"
                    [class.hover:border-subtle]="!isActiveTab('categories')"
                    [class.hover:bg-surface]="!isActiveTab('categories')"
                    [class.hover:text-text]="!isActiveTab('categories')"
                    [attr.aria-selected]="isActiveTab('categories')"
                    (click)="setActiveTab('categories')"
                    role="tab"
                  >
                    Categories
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border px-3 py-2.5 font-medium transition duration-200 ease-in-out"
                    [class.border-primary]="isActiveTab('actions')"
                    [class.bg-primary]="isActiveTab('actions')"
                    [class.text-primary-contrast]="isActiveTab('actions')"
                    [class.shadow-sm]="isActiveTab('actions')"
                    [class.border-transparent]="!isActiveTab('actions')"
                    [class.bg-transparent]="!isActiveTab('actions')"
                    [class.text-text-muted]="!isActiveTab('actions')"
                    [class.hover:border-subtle]="!isActiveTab('actions')"
                    [class.hover:bg-surface]="!isActiveTab('actions')"
                    [class.hover:text-text]="!isActiveTab('actions')"
                    [attr.aria-selected]="isActiveTab('actions')"
                    (click)="setActiveTab('actions')"
                    role="tab"
                  >
                    Actions
                  </button>
                </div>
              </div>

              <section
                class="bg-white px-4 py-5 sm:px-5"
                [hidden]="!isActiveTab('media')"
                role="tabpanel"
              >
                <div class="grid gap-4">
                  <div class="grid gap-2">
                    <label class="text-sm font-medium text-text" for="assetUpload">
                      Upload an asset (can be an image, audio or video clip)
                    </label>
                    <input
                      id="assetUpload"
                      (input)="onFileSelected($event)"
                      class="block w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text file:mr-4 file:rounded-lg file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-contrast"
                      type="file"
                    />
                    @if (selectedFileName()) {
                      <p class="text-sm text-text-muted">Selected file: {{ selectedFileName() }}</p>
                    }
                  </div>

                  <div class="grid gap-2">
                    <label class="text-sm font-medium text-text" for="assetPath">
                      Or point to its URL
                    </label>
                    <input
                      id="assetPath"
                      [(ngModel)]="formModel.assetPath"
                      (ngModelChange)="onAssetPathChanged()"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="assetPath"
                      placeholder="Enter Asset URL"
                      type="text"
                    />
                  </div>

                  <div class="grid gap-2">
                    <label class="text-sm font-medium text-text" for="numeticonSelect">
                      Or select a Numeticon
                    </label>
                    <select
                      id="numeticonSelect"
                      [(ngModel)]="formModel.subType"
                      (ngModelChange)="onSubTypeChanged()"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="subType"
                    >
                      <option value="">Select a Numeticon</option>
                      @for (option of numeticonOptions; track option.value) {
                        <option [value]="option.value">{{ option.label }}</option>
                      }
                    </select>
                  </div>

                  @if (showMediaError()) {
                    <p class="text-sm text-danger">
                      Select one media option: upload a file, enter an asset URL, or choose a
                      Numeticon.
                    </p>
                  }
                </div>
              </section>

              <section
                class="bg-white px-4 py-5 sm:px-5"
                [hidden]="!isActiveTab('source')"
                role="tabpanel"
              >
                <div class="grid gap-4">
                  <div class="grid gap-2">
                    <label class="text-sm font-medium text-text" for="source">
                      Acknowledge a source, if any
                    </label>
                    <input
                      id="source"
                      [(ngModel)]="formModel.source"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="source"
                      placeholder="Enter Source"
                      type="text"
                    />
                  </div>

                  <div class="grid gap-2">
                    <label class="text-sm font-medium text-text" for="url">
                      Provide a URL that points to additional information
                    </label>
                    <input
                      id="url"
                      [(ngModel)]="formModel.url"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="url"
                      placeholder="Enter URL"
                      type="text"
                    />
                  </div>
                </div>
              </section>

              <section
                class="bg-white px-4 py-5 sm:px-5"
                [hidden]="!isActiveTab('categories')"
                role="tabpanel"
              >
                <div class="grid gap-4">
                  @if (topicLoadErrorMessage()) {
                    <p class="text-sm text-text-muted">{{ topicLoadErrorMessage() }}</p>
                  }

                  <div class="grid gap-2">
                    <label class="text-sm font-medium text-text" for="topicSelect">
                      Please select an applicable topic <span class="text-danger">*</span>
                    </label>
                    <select
                      id="topicSelect"
                      [(ngModel)]="formModel.topicId"
                      #topicModel="ngModel"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      [class.border-danger]="
                        topicModel.invalid && (topicModel.touched || numeronForm.submitted)
                      "
                      name="topicId"
                      required
                    >
                      @for (option of resolvedTopicOptions(); track option.value) {
                        <option [value]="option.value">{{ option.label }}</option>
                      }
                    </select>
                    @if (topicModel.invalid && (topicModel.touched || numeronForm.submitted)) {
                      <p class="text-sm text-danger">Topic is required.</p>
                    }
                  </div>

                  <div class="grid gap-2">
                    <label class="text-sm font-medium text-text" for="categories">
                      Enter one or more applicable categories - separate with commas
                    </label>
                    <input
                      id="categories"
                      [(ngModel)]="formModel.categories"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="categories"
                      placeholder="Enter Categories"
                      type="text"
                    />
                  </div>
                </div>
              </section>

              <section
                class="bg-white px-4 py-5 sm:px-5"
                [hidden]="!isActiveTab('actions')"
                role="tabpanel"
              >
                <div class="grid gap-4">
                  <div class="grid gap-3 sm:grid-cols-[auto_1fr_2fr] sm:items-center sm:gap-4">
                    <input
                      [(ngModel)]="formModel.action1.enabled"
                      aria-label="Enable Call Now"
                      class="h-4 w-4 rounded border-subtle text-primary focus:ring-primary"
                      name="action1Enabled"
                      type="checkbox"
                    />
                    <span class="text-sm text-text">Call Now</span>
                    <input
                      [(ngModel)]="formModel.action1.value"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action1"
                      placeholder="Enter Tel #"
                      type="text"
                    />
                  </div>
                  @if (showActionValueError(formModel.action1)) {
                    <p class="text-sm text-danger">Call Now must include a telephone number.</p>
                  }

                  <div class="grid gap-3 sm:grid-cols-[auto_1fr_2fr] sm:items-center sm:gap-4">
                    <input
                      [(ngModel)]="formModel.action2.enabled"
                      aria-label="Enable Send Message"
                      class="h-4 w-4 rounded border-subtle text-primary focus:ring-primary"
                      name="action2Enabled"
                      type="checkbox"
                    />
                    <span class="text-sm text-text">Send Message</span>
                    <input
                      [(ngModel)]="formModel.action2.value"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action2"
                      placeholder="Enter email address"
                      type="text"
                    />
                  </div>
                  @if (showActionValueError(formModel.action2)) {
                    <p class="text-sm text-danger">Send Message must include an email address.</p>
                  }

                  <div class="grid gap-3 sm:grid-cols-[auto_1fr_2fr] sm:items-center sm:gap-4">
                    <input
                      [(ngModel)]="formModel.action3.enabled"
                      aria-label="Enable custom action 1"
                      class="h-4 w-4 rounded border-subtle text-primary focus:ring-primary"
                      name="action3Enabled"
                      type="checkbox"
                    />
                    <input
                      [(ngModel)]="formModel.action3.name"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action3Name"
                      placeholder="Enter name for custom action"
                      type="text"
                    />
                    <input
                      [(ngModel)]="formModel.action3.value"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action3"
                      placeholder="Enter URL"
                      type="text"
                    />
                  </div>
                  @if (showCustomActionNameError(formModel.action3)) {
                    <p class="text-sm text-danger">Custom action 1 must include a name.</p>
                  }
                  @if (showCustomActionValueError(formModel.action3)) {
                    <p class="text-sm text-danger">Custom action 1 must include a URL.</p>
                  }

                  <div class="grid gap-3 sm:grid-cols-[auto_1fr_2fr] sm:items-center sm:gap-4">
                    <input
                      [(ngModel)]="formModel.action4.enabled"
                      aria-label="Enable custom action 2"
                      class="h-4 w-4 rounded border-subtle text-primary focus:ring-primary"
                      name="action4Enabled"
                      type="checkbox"
                    />
                    <input
                      [(ngModel)]="formModel.action4.name"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action4Name"
                      placeholder="Enter name for custom action"
                      type="text"
                    />
                    <input
                      [(ngModel)]="formModel.action4.value"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action4"
                      placeholder="Enter URL"
                      type="text"
                    />
                  </div>
                  @if (showCustomActionNameError(formModel.action4)) {
                    <p class="text-sm text-danger">Custom action 2 must include a name.</p>
                  }
                  @if (showCustomActionValueError(formModel.action4)) {
                    <p class="text-sm text-danger">Custom action 2 must include a URL.</p>
                  }

                  <div class="grid gap-3 sm:grid-cols-[auto_1fr_2fr] sm:items-center sm:gap-4">
                    <input
                      [(ngModel)]="formModel.action5.enabled"
                      aria-label="Enable custom action 3"
                      class="h-4 w-4 rounded border-subtle text-primary focus:ring-primary"
                      name="action5Enabled"
                      type="checkbox"
                    />
                    <input
                      [(ngModel)]="formModel.action5.name"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action5Name"
                      placeholder="Enter name for custom action"
                      type="text"
                    />
                    <input
                      [(ngModel)]="formModel.action5.value"
                      class="w-full rounded-lg border border-subtle bg-white px-3 py-2 text-sm text-text outline-none transition duration-200 ease-in-out focus:border-primary focus:ring-2 focus:ring-primary/20"
                      name="action5"
                      placeholder="Enter URL"
                      type="text"
                    />
                  </div>
                  @if (showCustomActionNameError(formModel.action5)) {
                    <p class="text-sm text-danger">Custom action 3 must include a name.</p>
                  }
                  @if (showCustomActionValueError(formModel.action5)) {
                    <p class="text-sm text-danger">Custom action 3 must include a URL.</p>
                  }
                </div>
              </section>
            </div>

            @if (submitErrorMessage()) {
              <p class="text-sm text-danger">{{ submitErrorMessage() }}</p>
            }
          </div>

          <footer class="border-t border-subtle bg-surface px-6 py-4">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start">
              <button
                class="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-contrast transition duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="isSubmitting()"
                type="submit"
              >
                {{ submitButtonLabel() }}
              </button>

              <a
                class="inline-flex items-center justify-center rounded-lg border border-subtle bg-white px-4 py-2 text-sm font-medium text-text transition duration-200 ease-in-out hover:bg-surface"
                [routerLink]="cancelHref"
              >
                Cancel
              </a>
            </div>
          </footer>
        </article>
      </form>
    }
  </div>
</section>
